import React, { useCallback, useRef, useState } from "react";
import Cleave from "cleave.js/react";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Col,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import QuillFormField from "../../../../Components/Common/QuillFormField";
import {
  AddressLookupData,
  lookupAddressByPostalCode,
} from "../../../../services/addressLookupService";

export const DEFAULT_COUNTRY_CODE = "55";
export const DEFAULT_COUNTRY = "BR";

export interface UnitFormValues {
  name: string;
  phone_country_code: string;
  country: string;
  phone_display: string;
  postal_code_display: string;
  street: string;
  street_number: string;
  neighborhood: string;
  city: string;
  state: string;
  knowledge_base: string;
}

export const isBrazilianLocale = (locale: string): boolean => locale === "pt-BR";

interface UnitFormProps {
  formik: FormikProps<UnitFormValues>;
  editingUnitId?: number | null;
}

const UnitForm: React.FC<UnitFormProps> = ({ formik, editingUnitId = null }) => {
  const { t, i18n } = useTranslation();
  const hideCountryCode = isBrazilianLocale(i18n.language);
  const [lookupLoading, setLookupLoading] = useState(false);
  const lastLookupRef = useRef("");

  const applyAddressData = useCallback(
    (data: AddressLookupData) => {
      formik.setFieldValue("street", data.street || "");
      formik.setFieldValue("neighborhood", data.neighborhood || "");
      formik.setFieldValue("city", data.city || "");
      formik.setFieldValue("state", data.state || "");
    },
    [formik]
  );

  const lookupPostalCode = useCallback(
    async (rawValue: string) => {
      const digits = rawValue.replace(/\D/g, "");

      if (digits.length !== 8 || lastLookupRef.current === digits) {
        return;
      }

      lastLookupRef.current = digits;
      setLookupLoading(true);

      try {
        const response = await lookupAddressByPostalCode(digits);

        if (!response.success || !response.data) {
          toast.error(t("units.address.lookupFailed"));
          return;
        }

        const hasExisting =
          Boolean(formik.values.street.trim()) || Boolean(formik.values.city.trim());

        if (hasExisting) {
          const confirmed = window.confirm(t("units.address.replaceConfirm"));
          if (!confirmed) {
            return;
          }
        }

        applyAddressData(response.data);
      } catch {
        toast.error(t("units.address.lookupFailed"));
        lastLookupRef.current = "";
      } finally {
        setLookupLoading(false);
      }
    },
    [applyAddressData, formik.values.city, formik.values.street, t]
  );

  return (
    <>
      <div className="mb-3">
        <Label htmlFor="unit-name">{t("units.fields.name.label")}</Label>
        <Input
          id="unit-name"
          name="name"
          placeholder={t("units.fields.name.placeholder")}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={!!(formik.touched.name && formik.errors.name)}
        />
        {formik.touched.name && formik.errors.name ? (
          <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
        ) : null}
      </div>

      {hideCountryCode ? (
        <Input type="hidden" name="phone_country_code" value={DEFAULT_COUNTRY_CODE} readOnly />
      ) : null}
      <Input type="hidden" name="country" value={DEFAULT_COUNTRY} readOnly />

      <div className="mb-3">
        <Label htmlFor="unit-phone">{t("units.fields.phoneNumber.label")}</Label>
        {hideCountryCode ? (
          <Cleave
            id="unit-phone"
            className={`form-control${
              formik.touched.phone_display && formik.errors.phone_display ? " is-invalid" : ""
            }`}
            placeholder={t("units.fields.phoneNumber.placeholder")}
            options={{
              delimiters: ["(", ") ", "-"],
              blocks: [0, 2, 5, 4],
              numericOnly: true,
            }}
            value={formik.values.phone_display}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              formik.setFieldValue("phone_display", event.target.value)
            }
            onBlur={() => formik.setFieldTouched("phone_display", true)}
          />
        ) : (
          <InputGroup>
            <InputGroupText>+</InputGroupText>
            <Input
              id="unit-phone-country-code"
              name="phone_country_code"
              type="text"
              inputMode="numeric"
              placeholder={t("units.fields.phoneCountryCode.placeholder")}
              value={formik.values.phone_country_code}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
                formik.setFieldValue("phone_country_code", digits);
              }}
              onBlur={() => formik.setFieldTouched("phone_country_code", true)}
              invalid={
                !!(formik.touched.phone_country_code && formik.errors.phone_country_code)
              }
              style={{ maxWidth: 88 }}
              aria-label={t("units.fields.phoneCountryCode.label")}
            />
            <Cleave
              id="unit-phone"
              className={`form-control${
                formik.touched.phone_display && formik.errors.phone_display ? " is-invalid" : ""
              }`}
              placeholder={t("units.fields.phoneNumber.placeholder")}
              options={{
                numericOnly: true,
              }}
              value={formik.values.phone_display}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue("phone_display", event.target.value)
              }
              onBlur={() => formik.setFieldTouched("phone_display", true)}
            />
          </InputGroup>
        )}
        {formik.touched.phone_country_code && formik.errors.phone_country_code ? (
          <div className="invalid-feedback d-block">
            {formik.errors.phone_country_code}
          </div>
        ) : null}
        {formik.touched.phone_display && formik.errors.phone_display ? (
          <div className="invalid-feedback d-block">{formik.errors.phone_display}</div>
        ) : null}
      </div>

      <div className="mb-3">
        <Label className="fw-semibold">{t("units.address.sectionTitle")}</Label>
        <p className="text-muted small mb-2">{t("units.address.sectionHelp")}</p>
        <Row className="g-2">
          <Col md={4}>
            <Label htmlFor="unit-postal-code">{t("units.address.postalCode")}</Label>
            <div className="position-relative">
              <Cleave
                id="unit-postal-code"
                className="form-control"
                placeholder={t("units.address.postalCodePlaceholder")}
                options={{ delimiters: ["-"], blocks: [5, 3], numericOnly: true }}
                value={formik.values.postal_code_display}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  formik.setFieldValue("postal_code_display", event.target.value);
                }}
                onBlur={() => {
                  formik.setFieldTouched("postal_code_display", true);
                  void lookupPostalCode(formik.values.postal_code_display);
                }}
              />
              {lookupLoading ? (
                <Spinner
                  size="sm"
                  className="position-absolute"
                  style={{ right: 10, top: 10 }}
                />
              ) : null}
            </div>
          </Col>
          <Col md={6}>
            <Label htmlFor="unit-street">{t("units.address.street")}</Label>
            <Input
              id="unit-street"
              name="street"
              value={formik.values.street}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Col>
          <Col md={2}>
            <Label htmlFor="unit-street-number">{t("units.address.number")}</Label>
            <Input
              id="unit-street-number"
              name="street_number"
              value={formik.values.street_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Col>
          <Col md={4}>
            <Label htmlFor="unit-neighborhood">{t("units.address.neighborhood")}</Label>
            <Input
              id="unit-neighborhood"
              name="neighborhood"
              value={formik.values.neighborhood}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Col>
          <Col md={5}>
            <Label htmlFor="unit-city">{t("units.address.city")}</Label>
            <Input
              id="unit-city"
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Col>
          <Col md={3}>
            <Label htmlFor="unit-state">{t("units.address.state")}</Label>
            <Input
              id="unit-state"
              name="state"
              maxLength={2}
              value={formik.values.state}
              onChange={(event) =>
                formik.setFieldValue("state", event.target.value.toUpperCase().slice(0, 2))
              }
              onBlur={formik.handleBlur}
              placeholder="UF"
            />
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Label htmlFor="unit-knowledge-base">{t("units.form.knowledge_base_label")}</Label>
        <p className="text-muted small mb-2">{t("units.form.knowledge_base_help")}</p>
        <QuillFormField
          key={editingUnitId ?? "new"}
          value={formik.values.knowledge_base}
          onChange={(html) => formik.setFieldValue("knowledge_base", html)}
          onBlur={() => formik.setFieldTouched("knowledge_base", true)}
          editorHeight={220}
        />
      </div>
    </>
  );
};

export default UnitForm;
